import {
  AdminLayout,
  useDebounceFunction,
  useLocalStorage,
  useMediaQuery,
  useTranslation,
  useUrlParam,
} from "@opendash/core";
import { Icon } from "@opendash/icons";
import { AdminToolbar } from "@opendash/ui";
import { Button, Flex } from "antd";
import { observer } from "mobx-react-lite";
import Parse from "parse";
import * as React from "react";
import {
  FilterDrawer,
  FilterParams,
  FilterSortTags,
  KanbanContextProvider,
  KanbanManager,
  useTicketManager,
} from "../../features/tickets";

export const TicketsPage = observer(() => {
  const t = useTranslation();
  const [reload, setReload] = React.useState(false);

  const [userFilter, setUserFilter] = useLocalStorage<
    { key: string; params: FilterParams }[]
  >("openservice:filter_user");
  const [dataFilter, setDataFilter] = useLocalStorage<
    { key: string; params: FilterParams }[]
  >("openservice:filter_data");
  const [sortBy, setSortBy] = useLocalStorage<{
    sort: string;
    order: "asc" | "desc";
  }>("openservice:sortBy");

  // fetches tickets for the user and user role wich is used in the filter drawer component
  const userTicketManager = useTicketManager({
    filters: {
      user: { attribute: "user", value: Parse.User.current()?.id }, // the default filter to only see the tickets assigned to the current user
    },
    filterJunction: "or", // for users and roles we want an OR junction, so the user always sees his tickets and the tickets of his role if activated in the filter
  });

  // used to fetch all tickets with an and junction for the different fields that can be used in the filter drawer
  const dataTicketManager = useTicketManager();
  const debounceRefreshColumns = useDebounceFunction(
    () => kanbanManager.reload(),
    500
  );

  const generalFilters = React.useMemo(() => {
    const filters: Record<string, FilterParams> = {};
    if (!dataFilter) return filters;
    for (const { key, params } of dataFilter) filters[key] = params;
    for (const { key, params } of userFilter) filters[key] = params;
    return filters;
  }, [dataFilter, userFilter]);

  // kanban manager to handle the kanban view and general default filters
  const kanbanManager = React.useMemo(
    () =>
      new KanbanManager({
        defaultView: "state",
        viewtype: "list",
        lskey: "my-tickets",
        defaultTicketManagerConfig: {
          filterJunction: "or",
        },
      }),
    []
  );

  React.useEffect(() => {
    kanbanManager.setDefaultTicketManagerConfig({
      filters: generalFilters,
      filterJunction: "or",
      sortBy: sortBy?.sort,
      order: sortBy?.order,
    });

    kanbanManager.reload();
  }, [generalFilters, sortBy?.sort, sortBy?.order]);

  const [searchString, setSearchString] = useUrlParam<string>(
    "searchTickets",
    "",
    "string"
  );
  const isBelowWidth = useMediaQuery("(max-width: 576px)");
  const [filterOpen, setFilterOpen] = React.useState(false);

  // combines the two ticket managers to get the tickets for the user and the other fields
  React.useEffect(() => {
    setLocalStorage();
  }, [reload]);

  React.useEffect(() => {
    initLocalStorageData();
  }, []);
  React.useEffect(() => {
    debounceRefreshColumns();
  }, [searchString]);

  function setLocalStorage() {
    const dataFilter: { key: string; params: FilterParams }[] =
      dataTicketManager
        .getFilters()
        .map(([key, params]) => ({ key: key, params: params }));
    const userFilter: { key: string; params: FilterParams }[] =
      userTicketManager
        .getFilters()
        .map(([key, params]) => ({ key: key, params: params }));

    const sort = userTicketManager.getSortBy();
    const order = userTicketManager.getOrder();
    setSortBy({ sort: sort, order: order });
    setDataFilter(dataFilter);
    setUserFilter(userFilter);
  }

  function initLocalStorageData() {
    if (userFilter)
      for (const filter of userFilter)
        userTicketManager.addFilter(filter.key, filter.params);

    if (dataFilter)
      for (const filter of dataFilter)
        dataTicketManager.addFilter(filter.key, filter.params);

    if (sortBy) {
      userTicketManager.setSortBy(sortBy.sort);
      userTicketManager.setOrder(sortBy.order);
    }
    setReload((prev) => !prev);
  }

  return (
    <AdminLayout contentPadding>
      <AdminToolbar
        title={t("maintenance:home.headline")}
        onSearch={setSearchString}
        search={searchString}
        actions={
          <Button
            icon={<Icon icon="fa:filter" />}
            type="text"
            onClick={(e) => {
              setFilterOpen(true);
            }}
            style={{
              display: "block",
              height: "100%",
              color:
                dataTicketManager.getSortBy() ||
                dataTicketManager.hasFilterActive
                  ? "orange"
                  : "#000000",
            }}
          />
        }
        children={
          <Flex
            gap={12}
            vertical={isBelowWidth}
            style={{ marginBottom: 12 }}
            align="flex-start"
          >
            <FilterSortTags
              sortManager={userTicketManager}
              filterManager={dataTicketManager}
              reload={() => {
                setLocalStorage();
                setReload((prev) => !prev);
              }}
            />
          </Flex>
        }
      />
      <div className="od-page-main">
        <FilterDrawer
          showGroupSwitch
          showSubordinateSwitch
          showCreatedUserSwitch
          userManager={userTicketManager}
          sortManager={userTicketManager}
          subordinateManager={userTicketManager}
          open={filterOpen}
          setOpen={setFilterOpen}
          refetch={() => {
            setReload((prev) => !prev);
          }}
        />
      </div>

      <KanbanContextProvider value={kanbanManager}>
        {kanbanManager.getViewComponent()}
      </KanbanContextProvider>
    </AdminLayout>
  );
});
